import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { Tooltip } from 'primereact/tooltip';
import { AppCard, AppPage } from '../../components/general';
import { useFeeTracing } from '../../hooks/useFeeTracing';
import { GroupedTraceLogs, ProcessedTraceLog } from '../../models/tracing';
import { toLocalDateTimePrecise } from '../../utils/date-helper';
import TraceLogDetailPanel from '../../components/tracing/TraceLogDetailPanel';
import styles from '../../components/tracing/TraceLogList.module.css';

const FeeTracingDetails = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();

  const { traceLogsQuery, groupedLogs } = useFeeTracing(transactionId);

  const [selectedLog, setSelectedLog] = useState<ProcessedTraceLog | null>(null);
  const [sidePanelVisible, setSidePanelVisible] = useState(false);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized && groupedLogs && groupedLogs.length > 0) {
      const allGroupIds = new Set<string>();
      
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
      
      setExpandedGroups(allGroupIds);
      setHasInitialized(true);
    }
  }, [groupedLogs, hasInitialized]);

  const getAllGroupIds = () => {
    const allGroupIds = new Set<string>();
    
    if (groupedLogs) {
      groupedLogs.forEach((group) => {
        if (group.traceGroup && !group.traceGroup.includes(':')) {
          allGroupIds.add(`tier2-${group.traceGroup}`);
          
          if (group.children && group.children.length > 0) {
            allGroupIds.add(`tier3-${group.traceGroup}`);
          }
        }
      });
    }
    
    return allGroupIds;
  };

  const handleExpandAll = () => {
    const allGroupIds = getAllGroupIds();
    setExpandedGroups(allGroupIds);
  };

  const handleCollapseAll = () => {
    setExpandedGroups(new Set());
  };

  const handleViewDetails = (log: ProcessedTraceLog) => {
    setSelectedLog(log);
    setSidePanelVisible(true);
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const formatDateTime = (timestamp: string) => {
    return toLocalDateTimePrecise(timestamp) || timestamp;
  };

  const renderLog = (log: ProcessedTraceLog, indentLevel: number = 0, isLastInGroup: boolean = false, isTier3InLastTier2: boolean = false) => {
    const hasDetails = !!(log.contextProperties || log.eventDataFileId);
    const descriptionIndent = indentLevel * 3; // 3rem per level
    
    const bgColors = ['transparent', '#fafafa', '#f5f5f5', '#f0f0f0'];
    const bgColor = bgColors[indentLevel] || bgColors[bgColors.length - 1];

    return (
      <div 
        key={`${log.id}`} 
        className={`flex justify-content-between align-items-center py-2 hover:bg-blue-50 ${hasDetails ? 'cursor-pointer' : ''}`}
        style={{ backgroundColor: bgColor }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest('.trace-group-chip')) {
            return;
          }
          if (hasDetails) {
            handleViewDetails(log);
          }
        }}
      >
        <div className="flex gap-3 align-items-center flex-1">
          <div className={styles.chevronSpacer} />
          
          <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
            {formatDateTime(log.eventTimestamp)}
          </span>
          <span className={`text-base flex-1 ${styles.description}`} style={{ marginLeft: indentLevel > 0 ? `calc(${descriptionIndent}rem + 8px)` : `${descriptionIndent}rem` }}>
            {indentLevel === 1 && (
              <>
                {/* Vertical line */}
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent / 2}rem`,
                  top: '-0.5rem',
                  bottom: isLastInGroup ? '50%' : '-0.5rem',
                }} />
                {/* Horizontal connector */}
                <div className={styles.horizontalLine} style={{
                  left: `${-descriptionIndent / 2}rem`,
                  top: '50%',
                  width: `${descriptionIndent / 2}rem`,
                }} />
              </>
            )}
            {indentLevel === 2 && (
              <>
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent + (3 / 2)}rem`,
                  top: '-0.5rem',
                  bottom: (isLastInGroup && isTier3InLastTier2) ? '50%' : '-0.5rem',
                }} />
                <div className={styles.verticalLine} style={{
                  left: `${-descriptionIndent / 4}rem`,
                  top: '-0.5rem',
                  bottom: isLastInGroup ? '50%' : '-0.5rem',
                }} />
                <div className={styles.horizontalLine} style={{
                  left: `${-descriptionIndent / 4}rem`,
                  top: '50%',
                  width: `${descriptionIndent / 4}rem`,
                }} />
              </>
            )}
            {log.description || 'Event'}
          </span>
          {log.traceGroup && (
            <>
              <Tag 
                value={log.traceGroup} 
                className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
                data-pr-tooltip={log.traceGroupDescription || log.description || log.traceGroup}
                data-pr-position="top"
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderFlatLogs = (groups: GroupedTraceLogs[]) => {
    const tier1Logs: ProcessedTraceLog[] = [];
    const tier2ByGroup: Map<string, ProcessedTraceLog[]> = new Map();
    const tier3ByParent: Map<string, ProcessedTraceLog[]> = new Map();
    
    groups.forEach((group) => {
      group.logs.forEach((log) => {
        if (!log.traceGroup) {
          tier1Logs.push(log);
        } else if (!log.traceGroup.includes(':')) {
          // Tier 2
          if (!tier2ByGroup.has(log.traceGroup)) {
            tier2ByGroup.set(log.traceGroup, []);
          }
          tier2ByGroup.get(log.traceGroup)!.push(log);
        }
      });
      
      // Collect tier 3 logs
      if (group.traceGroup && !group.traceGroup.includes(':') && group.children && group.children.length > 0) {
        const parentGroup = group.traceGroup;
        if (!tier3ByParent.has(parentGroup)) {
          tier3ByParent.set(parentGroup, []);
        }
        group.children.forEach((childGroup) => {
          tier3ByParent.get(parentGroup)!.push(...childGroup.logs);
        });
      }
    });
    
    tier1Logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime());
    tier2ByGroup.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));
    tier3ByParent.forEach((logs) => logs.sort((a, b) => new Date(a.eventTimestamp).getTime() - new Date(b.eventTimestamp).getTime()));
    
    const timeline: Array<{ type: 'tier1' | 'tier2Group'; log: ProcessedTraceLog; groupLogs?: ProcessedTraceLog[] }> = [];
    
    tier1Logs.forEach((log) => {
      timeline.push({ type: 'tier1', log });
    });
    
    tier2ByGroup.forEach((logs, groupId) => {
      timeline.push({ type: 'tier2Group', log: logs[0], groupLogs: logs });
    });
    
    timeline.sort((a, b) => new Date(a.log.eventTimestamp).getTime() - new Date(b.log.eventTimestamp).getTime());
    
    const allElements: JSX.Element[] = [];
    
    timeline.forEach((item) => {
      if (item.type === 'tier1') {
        allElements.push(renderLog(item.log));
      } else if (item.type === 'tier2Group' && item.groupLogs) {
        const groupId = item.log.traceGroup!;
        const tier2Logs = item.groupLogs;
        const firstTier2 = tier2Logs[0];
        const tier2GroupId = `tier2-${groupId}`;
        const isTier2Expanded = expandedGroups.has(tier2GroupId);
        
        allElements.push(
          <div 
            key={`toggle-${tier2GroupId}`}
            className={`flex align-items-center py-2 hover:bg-blue-50 ${styles.toggleRow}`}
          >
            <div className="flex gap-3 align-items-center flex-1">
              <div 
                className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleGroup(tier2GroupId);
                }}
              >
                <i 
                  className={`pi ${isTier2Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-600 ${styles.chevronIcon}`}
                />
              </div>
              <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
                {formatDateTime(firstTier2.eventTimestamp)}
              </span>
              <span className={`text-base flex-1 ${styles.description}`}>
                {firstTier2.traceGroupDescription || groupId}
              </span>
              <Tag 
                value={groupId} 
                className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
                data-pr-tooltip={firstTier2.traceGroupDescription || groupId}
                data-pr-position="top"
              />
            </div>
          </div>
        );
        
        if (isTier2Expanded) {
          const nestedTimeline: Array<{ type: 'tier2' | 'tier3Group'; log: ProcessedTraceLog; tier3Logs?: ProcessedTraceLog[] }> = [];
          
          tier2Logs.forEach((log) => {
            nestedTimeline.push({ type: 'tier2', log });
          });
          
          if (tier3ByParent.has(groupId)) {
            const tier3Logs = tier3ByParent.get(groupId)!;
            nestedTimeline.push({ type: 'tier3Group', log: tier3Logs[0], tier3Logs });
          }
          
          nestedTimeline.sort((a, b) => new Date(a.log.eventTimestamp).getTime() - new Date(b.log.eventTimestamp).getTime());
          
          nestedTimeline.forEach((nestedItem, nestedIndex) => {
            const isLastNested = nestedIndex === nestedTimeline.length - 1;
            
            if (nestedItem.type === 'tier2') {
              allElements.push(renderLog(nestedItem.log, 1, isLastNested));
            } else if (nestedItem.type === 'tier3Group' && nestedItem.tier3Logs) {
              const tier3Logs = nestedItem.tier3Logs;
              const firstTier3 = tier3Logs[0];
              const tier3GroupId = `tier3-${groupId}`;
              const isTier3Expanded = expandedGroups.has(tier3GroupId);
              
              const descriptionIndent = 1 * 3;
                allElements.push(
                  <div 
                    key={`toggle-${tier3GroupId}`}
                    className={`flex align-items-center py-2 ${styles.tier3ToggleRow}`}
                >
                  <div className="flex gap-3 align-items-center flex-1">
                    <div 
                      className={`chevron-area flex align-items-center justify-content-center cursor-pointer ${styles.chevronArea}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleGroup(tier3GroupId);
                      }}
                    >
                      <i 
                        className={`pi ${isTier3Expanded ? 'pi-chevron-down' : 'pi-chevron-right'} text-gray-600 ${styles.chevronIcon}`}
                      />
                    </div>
                    <span className={`text-sm text-gray-500 font-mono ${styles.timestamp}`}>
                      {formatDateTime(firstTier3.eventTimestamp)}
                    </span>
                    <span className={`text-base flex-1 ${styles.description}`} style={{ marginLeft: `calc(${descriptionIndent}rem + 8px)` }}>
                      <div className={styles.verticalLine} style={{
                        left: `${-descriptionIndent / 2}rem`,
                        top: '-0.5rem',
                        bottom: isLastNested ? '50%' : '-0.5rem',
                      }} />
                      <div className={styles.horizontalLine} style={{
                        left: `${-descriptionIndent / 2}rem`,
                        top: '50%',
                        width: `${descriptionIndent / 2}rem`,
                      }} />
                      {firstTier3.traceGroupDescription || firstTier3.traceGroup}
                    </span>
                    {firstTier3.traceGroup && (
                      <>
                        <Tag 
                          value={firstTier3.traceGroup} 
                          className={`text-xs font-mono trace-group-chip ${styles.traceGroupChip}`}
                          data-pr-tooltip={firstTier3.traceGroupDescription || firstTier2.description || firstTier3.traceGroup}
                          data-pr-position="top"
                        />
                      </>
            )}
          </div>
                </div>
              );

                if (isTier3Expanded) {
                  tier3Logs.forEach((t3Log, t3Index) => {
                    const isLastT3 = t3Index === tier3Logs.length - 1;
                    allElements.push(renderLog(t3Log, 2, isLastT3, isLastNested));
                  });
                }
            }
          });
        }
      }
    });
    
    return allElements;
  };

  if (traceLogsQuery.isLoading) {
    return (
      <AppPage title="Fee Transaction Log Details" breadcrumbs="Home - Fee Transactions Logs - Details">
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2 p-6">
            <ProgressSpinner className={styles.spinnerContainer} strokeWidth="4" animationDuration=".5s" />
            <span>Loading trace logs...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  if (traceLogsQuery.isError) {
    return (
      <AppPage title="Fee Transaction Log Details" breadcrumbs="Home - Fee Transactions Logs - Details">
        <AppCard>
          <div className="flex flex-column gap-3">
            <div className="text-red-500">
              <i className="pi pi-exclamation-triangle mr-2" />
              Error loading trace logs: {traceLogsQuery.error instanceof Error ? traceLogsQuery.error.message : 'Unknown error'}
            </div>
            <div>
              <Button
                label="Back to Search"
                icon="pi pi-arrow-left"
                onClick={() => navigate('/fee-tracing')}
              />
            </div>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  const firstLog = traceLogsQuery.data?.[0];

  return (
    <AppPage title="Fee Transaction Logs" breadcrumbs="Home - Fee Transactions Logs - Details">
      <div className="flex flex-column gap-3">
        <AppCard>
          <div className="flex justify-content-between align-items-start">
            <div className="flex flex-column gap-2">
              <div>
                <span className="font-semibold mr-2">Transaction ID:</span>
                <code className="bg-gray-100 p-1 rounded text-sm">{transactionId}</code>
              </div>
              {firstLog?.loanNumber && (
                <div>
                  <span className="font-semibold mr-2">Loan Number:</span>
                  <span>{firstLog.loanNumber}</span>
                </div>
              )}
              {firstLog?.loanIdentifier && (
                <div>
                  <span className="font-semibold mr-2">Loan Identifier:</span>
                  <span>{firstLog.loanIdentifier}</span>
                </div>
              )}
              <div>
                <span className="font-semibold mr-2">Total Events:</span>
                <Tag value={traceLogsQuery.data?.length || 0} severity="info" />
              </div>
            </div>
            <Button
              label="Back to Search"
              icon="pi pi-arrow-left"
              onClick={() => navigate('/fee-tracing')}
              outlined
            />
          </div>
        </AppCard>

        <AppCard>
          <div className="flex justify-content-between align-items-center mb-3">
            <div className="flex gap-2">
              <Button
                label="Expand All"
                icon="pi pi-plus"
                onClick={handleExpandAll}
                size="small"
                outlined
              />
              <Button
                label="Collapse All"
                icon="pi pi-minus"
                onClick={handleCollapseAll}
                size="small"
                outlined
              />
            </div>
          </div>
          <Tooltip target=".trace-group-chip" />
          
          {groupedLogs.length === 0 ? (
            <div className="text-center text-gray-500 py-6">
              No trace logs found for this transaction.
            </div>
          ) : (
            <div className="trace-log-list">
              {renderFlatLogs(groupedLogs)}
            </div>
          )}
        </AppCard>
      </div>

      <Sidebar 
        visible={sidePanelVisible}
        position="right"
        onHide={() => setSidePanelVisible(false)}
        className={`trace-log-sidebar ${styles.sidebarPanel}`}
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-list text-xl" />
            <span className="font-semibold">Trace Log Details</span>
          </div>
        }
      >
        {selectedLog && <TraceLogDetailPanel log={selectedLog} />}
      </Sidebar>
    </AppPage>
  );
};

export default FeeTracingDetails;
